/**
 * Banner Carousel Component
 * Adds carousel functionality to large banner cards in the banners-with-categories section
 */
if (!customElements.get('banner-carousel')) {
  customElements.define(
    'banner-carousel',
    class BannerCarousel extends HTMLElement {
      constructor() {
        super();
        this.carousel = null;
        this.autoplayEnabled = this.dataset.autoplay === 'true';
        this.autoplayDelay = parseInt(this.dataset.autoplayDelay) || 5000;
        this.pauseOnHover = this.dataset.pauseOnHover === 'true';
        this.isMobile = window.matchMedia('(max-width: 767px)').matches;
        this.hasInteracted = false;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        // Check if required dependencies are available
        if (typeof FoxTheme === 'undefined' || typeof FoxTheme.Carousel === 'undefined') {
          console.error('FoxTheme.Carousel is not available');
          return;
        }
        
        // The banner-carousel element itself is the swiper container
        const swiperElement = this;

        // Subtask 3.1: Initialize Swiper with base configuration
        const config = {
          // Base configuration
          slidesPerView: 1,
          spaceBetween: 0,
          speed: 600,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          threshold: 2,

          // Subtask 3.3: Configure pagination (dot indicators)
          pagination: {
            el: '.banner-carousel__pagination',
            clickable: true,
            bulletClass: 'banner-carousel__dot',
            bulletActiveClass: 'banner-carousel__dot--active',
          },

          // Subtask 3.2: Configure autoplay functionality
          // Autoplay is disabled on mobile and only enabled if setting is true
          autoplay:
            this.autoplayEnabled && !this.isMobile
              ? {
                  delay: this.autoplayDelay,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: this.pauseOnHover,
                }
              : false,

          // Subtask 3.4: Configure keyboard navigation
          keyboard: {
            enabled: true,
            onlyInViewport: true,
          },

          // Subtask 3.5: Implement accessibility features
          a11y: {
            enabled: true,
            prevSlideMessage: 'Previous banner',
            nextSlideMessage: 'Next banner',
            paginationBulletMessage: 'Go to banner {{index}}',
            slideRole: 'group',
          },

          on: {
            init: this.handleAfterInit.bind(this),
            slideChange: this.handleSlideChange.bind(this),
            // Subtask 3.6: Handle swipe gestures on mobile
            touchStart: this.handleTouchStart.bind(this),
            // Task 6.1: Preload next slide image on transition
            slideChangeTransitionEnd: this.preloadNextSlide.bind(this),
          },
        };

        // Initialize carousel using FoxTheme.Carousel wrapper
        // Try with NO additional modules first to see if it initializes
        this.carousel = new window.FoxTheme.Carousel(swiperElement, config);
        this.carousel.init();

        // Subtask 3.2: Reset timer on manual navigation (dot clicks)
        if (this.carousel.slider) {
          const pagination = this.querySelector('.banner-carousel__pagination');
          if (pagination) {
            pagination.addEventListener('click', this.handleManualNavigation.bind(this));
          }

          // Handle keyboard navigation to reset timer
          this.carousel.slider.on('keyPress', this.handleManualNavigation.bind(this));
        }
      }

      // Subtask 3.5: Add ARIA labels and accessibility attributes
      handleAfterInit() {
        const wrapper = this.querySelector('.swiper-wrapper');
        if (wrapper) {
          wrapper.setAttribute('role', 'region');
          wrapper.setAttribute('aria-label', 'Banner carousel');
          wrapper.setAttribute('aria-live', 'polite');
        }

        // Announce initial slide
        this.announceSlideChange();
      }

      // Subtask 3.5: Announce slide changes to screen readers
      handleSlideChange() {
        this.announceSlideChange();
      }

      announceSlideChange() {
        if (!this.carousel || !this.carousel.slider) return;

        const currentSlide = this.carousel.slider.realIndex + 1;
        const totalSlides = this.carousel.slider.slides.length;

        // Update aria-label for screen reader announcement
        const announcement = `Banner ${currentSlide} of ${totalSlides}`;
        this.setAttribute('aria-label', announcement);

        // Also update the wrapper's aria-live region
        const wrapper = this.querySelector('.swiper-wrapper');
        if (wrapper) {
          wrapper.setAttribute('aria-label', announcement);
        }
      }

      // Subtask 3.2: Reset autoplay timer on manual navigation
      handleManualNavigation() {
        if (this.carousel && this.carousel.slider && this.carousel.slider.autoplay) {
          // Stop and restart autoplay to reset the timer
          this.carousel.slider.autoplay.stop();
          this.carousel.slider.autoplay.start();
        }
      }

      // Subtask 3.6: Disable autoplay after first swipe interaction on mobile
      handleTouchStart() {
        if (this.isMobile && !this.hasInteracted) {
          this.hasInteracted = true;
          if (this.carousel && this.carousel.slider && this.carousel.slider.autoplay) {
            this.carousel.slider.autoplay.stop();
          }
        }
      }

      // Task 6.1: Preload next slide image on slideChange event
      preloadNextSlide() {
        if (!this.carousel || !this.carousel.slider) return;

        const nextIndex = this.carousel.slider.activeIndex + 1;
        const slides = this.carousel.slider.slides;

        if (nextIndex < slides.length) {
          const nextSlide = slides[nextIndex];
          if (nextSlide) {
            // Find all images in the next slide
            const images = nextSlide.querySelectorAll('img[loading="lazy"]');
            images.forEach((img) => {
              // Change loading attribute to eager to trigger preload
              if (img.loading === 'lazy') {
                img.loading = 'eager';
              }
            });
          }
        }
      }

      disconnectedCallback() {
        if (this.carousel && this.carousel.slider) {
          this.carousel.slider.destroy();
        }
      }
    }
  );
}
